<script module>
import WarningMessage from "../components/WarningMessage.svelte";
import Map from "../components/Map.svelte";
import { getContext } from 'svelte';
import * as lib from '$lib/lib.js';
import { filterLocations } from '$lib/locations.js';
import { _ } from 'svelte-i18n';
</script>

<script>
import * as log from '$lib/log.js';
import { filter_default } from '$lib/filter.js';
import { appState } from '$lib/state.svelte.js';
import { fade } from 'svelte/transition';
import { cubicOut } from 'svelte/easing';
import { scrubNumber } from '$lib/scrub.js';

  // Collapses the subscriptions section while fading it out, so the content
  // below glides up during the animation instead of jumping when the
  // elements are finally removed from the DOM.
  /**
   * @param {HTMLElement} node
   * @param {{ duration?: number }} [options]
   */
  function fadeCollapse(node, { duration = 250 } = {}) {
    const height = node.offsetHeight;
    return {
      duration,
      easing: cubicOut,
      css: (/** @type {number} */ t) => `opacity: ${t}; overflow: hidden; height: ${t * height}px;`
    };
  }

  // In SvelteKit, the result of your load function is passed as a prop named
  // data.
  let { data } = $props();
  appState.init(data);

  let showNoTelegram = $state(false);
  let map = $state();
  let error = $state();
  let message = $state({ text: "", color: "", ok: true });

  var filter = $state({
    type: 'rent',
    area: [
      filter_default.rent.area.min,
      filter_default.rent.area.max
    ],
    price: [
      filter_default.rent.price.min,
      filter_default.rent.price.max
    ]
  });

  $effect(() => {
    const type = filter.type;

    filter.area = [
      filter_default[type].area.min,
      filter_default[type].area.max,
    ];
    filter.price = [
      filter_default[type].price.min,
      filter_default[type].price.max,
    ];
  });

  function fill_area_range(min, max) {
    document.getElementById("miar").value = min;
    document.getElementById("maar").value = max;
  }

  function fill_price_range(min, max) {
    document.getElementById("mipr").value = min;
    document.getElementById("mapr").value = max;
  }

  function clear_price_range() {
    document.getElementById("mipr").value = "";
    document.getElementById("mapr").value = "";
  }

  function clear_area_range() {
    document.getElementById("miar").value = "";
    document.getElementById("maar").value = "";
  }

  export function subscribeCallback() {
      message = lib.subscribe(appState, filter);
      lib.haptic('notificationOccurred', message.ok ? 'success' : 'error');
  }

  export function doneCallback() {
      lib.haptic('impactOccurred', 'light');
      map.doneMapCallback()
  }

  export function invalidRegionCallback() {
      message = {
          text: $_('page.region_invalid', { default: 'The drawn region is not valid, draw it again' }),
          color: "var(--tg-theme-destructive-text-color)",
          ok: false
      };
  }

  /**
   * @param {number} sidx
   */
  function removeCallback(sidx) {
      lib.haptic('notificationOccurred', 'warning');
      lib.unsubscribe(appState, sidx);
  }

  lib.init().then(async () => {
    if (appState.log.length > 0) {
      // Sync pending journal events on open
      lib.sync_pending(appState);
    } else {
      lib.sync({}).then((json) => {
        log.merge_backend_state(appState, json);
      }).catch((reason) => {
        console.log(`Failed to fetch subscriptions: ${reason.message}`);
      });
    }
    console.log(appState);
    var text = $_('map.telegram_main_button_subscribe', { default: 'Subscribe' });
    lib.setTelegramButton(text, subscribeCallback);
  }).catch((e) => {
    error = e;
    showNoTelegram = true;
  });
</script>

{#if showNoTelegram}
<div class="block section" id="no-telegram-message">
  <span style="color: var(--tg-theme-destructive-text-color);">
    {$_('page.error_message', { default: 'Can not communicate to Telegram.' })}
  </span>
  <details>
    <summary>
      {$_('error.error_summary', { default: 'Error details' })}
    </summary>
    <pre>{ error }</pre>
  </details>
  <br>
  {$_('error.error_report', { default: 'Report to' })}
  <a href="https://t.me/nemambyt_support_bot">@nemambyt_support_bot</a>
</div>
{/if}

{#if appState.subscriptions.length !== 0}
  <div class="subs-section" out:fadeCollapse={{ duration: 250 }}>
  <h2 class="section_header">
    {$_('page.subscriptions', { default: 'Subscriptions' })}
  </h2>
  <div class="section section-round">
    <div id="subs-block">
      {#each appState.subscriptions as subscription, sidx}
          <div class="subscription" in:fade={{ duration: 250 }}>

            <div class="subscription-info">
<div class="subscription-title">
              <div class="subscription-title-text">
                {#if subscription.advert_type === 'rent'}
                  {$_('page.advert_type_rent', { default: 'Renting' })}
                {:else}
                  {$_('page.advert_type_buy', { default: 'Buying' })}
                {/if}
                { lib.formatFloorplans(subscription.floorplans, filter_default[subscription.advert_type].floorplans.length) }
              </div>
              {#if subscription.metadata?.status === log.SubscriptionStatus.Pending}
                <svg class="pending-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"
                  fill="none" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                  role="img"
                  aria-label="{$_('page.pending_subscription', { default: 'Not synced yet' })}">
                  <polyline points="16 16 12 12 8 16"/>
                  <line x1="12" y1="12" x2="12" y2="21"/>
                  <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3"/>
                  <polyline points="16 16 12 12 8 16"/>
                </svg>
              {/if}
            </div>

            <div class="subscription-details">
              <div class="subscription-details-info">
                <div class="hint">
                  {#if subscription.sources.length == filter_default.sources.length }
                    {$_('page.all_sources', { default: 'from all sources' })}
                  {:else if subscription.sources.length > filter_default.sources.length - 4}
                    {$_('page.all_sources_but', { default: 'from all sources but' })}
                    { filter_default.sources.filter(s => !subscription.sources.includes(s)).map(s => filter_default.source_names[s]).join(", ") }
                  {:else}
                    {$_('page.from_sources', { default: 'from' })} { subscription.sources.map(s => filter_default.source_names[s]).join(", ") }
                  {/if} {$_('page.and', { default: 'and' })}
                    {#if subscription.no_duplicates}
                    {$_('page.no_duplicates', { default: 'no duplicates' })}
                  {:else}
                    {$_('page.with_duplicates', { default: 'with duplicates' })}
                  {/if}
                </div>

              <div class="subs-item">
                  <div class="subs-item-props">
                    <div id="price-area">
                      <span>
                        {#if subscription.price.min !== null && subscription.price.max !== null }
                          { lib.formatPrice(subscription.price.min, true) } - { lib.formatPrice(subscription.price.max, true) } Kč
                        {:else if subscription.price.min }
                          {$_('page.price_from', { default: 'from' })}
                          { lib.formatPrice(subscription.price.min, true) } Kč
                        {:else if subscription.price.max }
                          {$_('page.price_up_to', { default: 'up to' })}
                          { lib.formatPrice(subscription.price.max, true) } Kč
                        {:else }
                          {$_('page.price_any_price', { default: 'any price' })}
                        {/if}
                      </span>,
                      <span>
                        {#if subscription.area.min !== null && subscription.area.max !== null }
                          { subscription.area.min } - { subscription.area.max } m<sup>2</sup>
                        {:else if subscription.area.min }
                          {$_('page.area_from', { default: 'from' })}
                          { subscription.area.min } m<sup>2</sup>
                        {:else if subscription.area.max }
                          {$_('page.area_up_to', { default: 'up to' })}
                          { subscription.area.max } m<sup>2</sup>
                        {:else }
                          {$_('page.area_any_area', { default: 'any area' })}
                        {/if}
                      </span>
                    </div>

                    <div class="line-height-20">
                      {#if Object.keys(subscription.locations.custom).length == 0}
                        {#if subscription.locations.predefined == 0}
                          {$_('page.location_at_any_location', { default: 'at any location' })}
                        {:else}
                          { filterLocations.all[subscription.locations.predefined]?.[0] ?? $_('page.location_unknown', { default: 'Unknown location' }) }
                        {/if}
                      {:else}
                        {$_('page.location_drawn_region', { default: 'Drawn region' })}
                      {/if}
                    </div>
                  </div>

                </div>
              </div>

              <div class="subs-svg-wrap" in:fade={{ duration: 250 }}>
                {#if Object.keys(subscription.locations.custom).length !== 0}
                    {#snippet svgMap(svgData)}
                      {@const scale = 48 / svgData.height}
                      <svg class="subs-svg-map" viewBox={svgData.viewBox} preserveAspectRatio="xMidYMid meet"
                        style="width: {svgData.width * scale}px; height: {svgData.height * scale}px">
                        {#if svgData.cityPathData}
                          <path d={svgData.cityPathData} class="subs-svg-city" />
                        {/if}
                        <path d={svgData.pathData} class="leaflet-polygon-non-blurred" />
                      </svg>
                    {/snippet}
                    {@render svgMap(lib.generatePictogram(subscription.locations.custom))}
                {/if}
              </div>
            </div>
          </div>

          <div>
            <button
              id="remove-button"
              type="button"
              aria-label="{$_('page.remove_button_alt', { default: 'Remove subscription' })}"
              onclick={() => removeCallback(sidx)}
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 22"
                fill="none" stroke="currentColor" stroke-width="2"
                stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <polyline points="3 6 5 6 21 6" transform="translate(-2,-1)" />
                <path d="m 17,5 v 14 a 2,2 0 0 1 -2,2 H 5 A 2,2 0 0 1 3,19 V 5 M 6,5 V 3 A 2,2 0 0 1 8,1 h 4 a 2,2 0 0 1 2,2 v 2" />
                <line x1="8" y1="10" x2="8" y2="16" />
                <line x1="12" y1="10" x2="12" y2="16" />
              </svg>
            </button>
          </div>

      </div>

      {#if sidx !== (appState.subscriptions.length - 1)}
        <div class="section-separator-wrap">
          <hr class="section-separator">
        </div>
      {/if}
      {/each}
      </div>
  </div>
  </div>
{/if}

<h2 class="section_header">
  {$_('page.filter_header', { default: 'Create Subscription' })}
</h2>

<div class="section">
  <form id="filter-form" autocomplete="off">
    <div>
      <legend>
        {$_('page.filter_rent_or_buy', { default: 'Looking for' })}
      </legend>
      <fieldset id="filter-rent-or-buy-set">
        <div id="rent-or-buy-wrap">
          <label class="filter-type-cbox">
              <input type="radio" name="advert_type" value="rent" bind:group={filter.type} checked>
              <span>{$_('page.filter_rent', { default: 'Rent' })}</span>
          </label>
          <label class="filter-type-cbox">
            <input type="radio" name="advert_type" bind:group={filter.type} value="buy">
            <span>{$_('page.filter_buy', { default: 'Buy' })}</span>
          </label>
        </div>
      </fieldset>
    </div>

    <div>
      <legend>
        {$_('page.filter_floorplan', { default: 'Floor plan' })}
      </legend>
      <fieldset id="filter-type-cbox-set">
        <div id="floorplan-wrap">
          {#each filter_default[filter.type].floorplans as floorplan }
              <label class="filter-type-cbox">
                {#if floorplan === "2+kk" }
                  <input type="checkbox" name="floorplan" value="{ floorplan }" checked> <span>{ floorplan }</span>
                {:else}
                  <input type="checkbox" name="floorplan" value="{ floorplan }"> <span>{ floorplan }</span>
                {/if}
              </label>
          {/each}
        </div>
      </fieldset>
    </div>

    <div>
      <legend>
        {$_('page.filter_price', { default: 'Price' })}
      </legend>
      <fieldset class="range-fieldset">
        {#each filter_default[filter.type].ranges.price as pair }
          <button class="range-button" type="button" 
              onclick={() => fill_price_range(pair[0], pair[1])}>
            { lib.formatPrice(pair[0], true) } - { lib.formatPrice(pair[1], true) } Kč
          </button>
        {/each}
        <button class="range-button" type="button"
            onclick={() => clear_price_range()}>
          {$_('page.filter_any', { default: 'Any' })}
        </button>
        <div class="range-wrap">
          <input type="number"
                 id="mipr"
                 class="filter-field scrub-hint"
                 name="mipr"
                 step="{ filter_default[filter.type].price.step }"
                 inputmode="numeric"
                 use:scrubNumber={{
                   getStep: () => filter_default[filter.type].price.step,
                   getMin: () => filter_default[filter.type].price.min,
                   getMax: () => filter_default[filter.type].price.max,
                   getSiblingId: () => 'mapr',
                   siblingRole: 'min'
                 }} >
          &mdash;
          <input type="number"
                 id="mapr"
                 class="filter-field scrub-hint"
                 name="mapr"
                 step="{ filter_default[filter.type].price.step }"
                 inputmode="numeric"
                 use:scrubNumber={{
                   getStep: () => filter_default[filter.type].price.step,
                   getMin: () => filter_default[filter.type].price.min,
                   getMax: () => filter_default[filter.type].price.max,
                   getSiblingId: () => 'mipr',
                   siblingRole: 'max'
                 }} > Kč
        </div>
      </fieldset>
    </div>

    <div>
      <legend>
        {$_('page.filter_area', { default: 'Area' })}
      </legend>
      <fieldset class="range-fieldset">
        {#each filter_default[filter.type].ranges.area as pair }
          <button class="range-button" type="button" 
              onclick={() => fill_area_range(pair[0], pair[1])}>
              { pair[0] } - { pair[1] } m<sup>2</sup>
          </button>
        {/each}
        <button class="range-button" type="button"
            onclick={() => clear_area_range()}>
          {$_('page.filter_any', { default: 'Any' })}
        </button>
        <div class="range-wrap">
          <input type="number"
                 id="miar"
                 class="filter-field scrub-hint"
                 name="miar"
                 step="{ filter_default[filter.type].area.step }"
                 inputmode="numeric"
                 use:scrubNumber={{
                   getStep: () => filter_default[filter.type].area.step,
                   getMin: () => filter_default[filter.type].area.min,
                   getMax: () => filter_default[filter.type].area.max,
                   getSiblingId: () => 'maar',
                   siblingRole: 'min'
                 }} >
          &mdash;
          <input type="number"
                 id="maar"
                 class="filter-field scrub-hint"
                 name="maar"
                 step="{ filter_default[filter.type].area.step }"
                 inputmode="numeric"
                 use:scrubNumber={{
                   getStep: () => filter_default[filter.type].area.step,
                   getMin: () => filter_default[filter.type].area.min,
                   getMax: () => filter_default[filter.type].area.max,
                   getSiblingId: () => 'miar',
                   siblingRole: 'max'
                 }} >
                 <span>m<sup style="vertical-align: super;">2</sup></span>
        </div>
      </fieldset>
    </div>

    <div>
      <legend>
        {$_('page.filter_location', { default: 'Location' })}
      </legend>
      <Map bind:this={map} filter={ filter }
           subscribeCallback={ subscribeCallback }
           doneCallback={ doneCallback }
           invalidCallback={ invalidRegionCallback } />
    </div>

    <div>
      <legend>
        {$_('page.filter_sources', { default: 'Sources' })}
      </legend>
      <fieldset>
        <div id="source-wrap">
          {#each filter_default.sources as source}
          <label class="filter-type-cbox">
            <input type="checkbox" name="source" value="{ source }" checked> <span>{ filter_default.source_names[source] }</span>
          </label>
          {/each}
        </div>
      </fieldset>
    </div>

    <div>
      <legend>
        {$_('page.filter_other', { default: 'Other' })}
      </legend>
      <fieldset id="filter-other">
        <div id="other-wrap">
          <label class="filter-type-cbox filter-type-check">
            <input type="checkbox" name="no_duplicates" checked>
            <span>{$_('page.filter_other_dont_show_duplicates', { default: 'Don\'t show duplicates' })}</span>
          </label>
        </div>
      </fieldset>
    </div>
  </form>
</div>

<footer>
  <span class="hint">
    {$_('page.support', { default: 'Support' })}
    <a href="https://t.me/nemambyt_support_bot">@nemambyt_support_bot</a>
  </span>
</footer>

{#if message.text !== ""}
  {#key message}
    <WarningMessage message={message} onClose={() => { message = { text: "", color: "", ok: true }; }}/>
  {/key}
{/if}
